import {capitalize, getOwnAttr} from 'common/util';
import {MPProperties, PATH_SEPARATOR} from 'common/constants';
import type {TooltipAttrs} from 'common/components/tooltip/types';

import {getPropertyDisplayName, getPropertyDisplayNameOld} from '../display-name-registry';
import {FullPropertyType, MappedPropertyValues, PropertyType, ResourceType} from '../constants';
import {renameEvent} from './events';
import {getPropertyValueMappings} from './mappings';

type MetaOptions = {isCustom?: boolean};

export const propertiesUtils = {
  renameProperty,
};

export const objectAccessSymbol = PATH_SEPARATOR;
export const listObjectAccessSymbol = PATH_SEPARATOR;

export enum ConstantPropertyKeys {
  EmptyString = `(empty string)`,
  EmptyList = `(empty list)`,
  NotSet = `(not set)`,
  NotApplicable = `(not applicable)`,
  NotANumber = `(not a number)`,
}

export enum ConstantPropertyValues {
  EmptyString = ``,
  EmptyList = `$empty_list`,
  NotSet = `undefined`,
  NotApplicable = `$not_applicable`,
  NotANumber = `$non_numeric_values`,
  RemainingValues = `$remaining_values`,
}

export const ConstantPropertyMapper = {
  [ConstantPropertyKeys.EmptyString]: ConstantPropertyValues.EmptyString,
  [ConstantPropertyKeys.EmptyList]: ConstantPropertyValues.EmptyList,
  [ConstantPropertyKeys.NotSet]: ConstantPropertyValues.NotSet,
  [ConstantPropertyKeys.NotApplicable]: ConstantPropertyValues.NotApplicable,
  [ConstantPropertyKeys.NotANumber]: ConstantPropertyValues.NotANumber,
};

export const ConstantPropertyValueSet = new Set<ConstantPropertyValues>(Object.values(ConstantPropertyValues));

export type ConstantPropertyValueInfo = {label: string; tooltip?: TooltipAttrs};
export const ConstantPropertyValuesInfo: Record<ConstantPropertyValues, ConstantPropertyValueInfo> = {
  [ConstantPropertyValues.NotANumber]: {
    label: `(not a number)`,
    tooltip: {
      text: `This property contains an invalid number representation`,
    },
  },
  [ConstantPropertyValues.EmptyList]: {
    label: `(empty list)`,
    tooltip: {
      text: `This property is set to an empty list.\n[Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#list-property-support)`,
    },
  },
  [ConstantPropertyValues.NotSet]: {
    label: `(not set)`,
    tooltip: {
      text: `This property does not contain any value.\n[Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#undefined-and-null-properties)`,
    },
  },
  [ConstantPropertyValues.EmptyString]: {
    label: `(empty string)`,
    tooltip: {text: `This property is set to an empty string value`},
  },
  [ConstantPropertyValues.NotApplicable]: {
    label: `(not applicable)`,
  },
  [ConstantPropertyValues.RemainingValues]: {
    label: `(remaining values)`,
    tooltip: {text: `Includes all defined values that are ungrouped.`},
  },
  [ConstantPropertyValues.NotANumber]: {
    label: `(non-numeric values)`,
    tooltip: {text: `The data in this group are in a format that can not be read as numbers (e.g. string)`},
  },
};

export function getTooltipFromLabel(label) {
  const defaultProp = Object.values(ConstantPropertyValuesInfo).find((info) => info.label === label);
  return defaultProp ? defaultProp.tooltip.text : null;
}

export function getIconForPropertyType(
  type: PropertyType | FullPropertyType,
  {isCustom}: MetaOptions = {isCustom: false},
): string {
  const iconNameBuffer = [`type`, isCustom && `custom`].filter(Boolean);

  let simpleType: PropertyType;

  if (type && typeof type === `object`) {
    simpleType = type.isList ? PropertyType.List : type.type;
  } else {
    simpleType = type as PropertyType;
  }

  switch (simpleType) {
    case PropertyType.Dimension:
      return `table`;
    case PropertyType.String:
      iconNameBuffer.push(`text`);
      break;
    case PropertyType.Datetime:
      iconNameBuffer.push(`date`);
      break;
    case PropertyType.Boolean:
    case PropertyType.List:
    case PropertyType.Number:
    case PropertyType.Object:
      iconNameBuffer.push(simpleType);
      break;
    default:
      return null;
  }
  return iconNameBuffer.join(`-`);
}

const PROPERTY_TYPE_LABEL_MAP = {
  boolean: `True / False`,
  datetime: `Date`,
  list: `List`,
  number: `Number`,
  object: `Object`,
  string: `String`,
};

export function getLabelForPropertyType(type, {isCustom}: MetaOptions = {isCustom: false}) {
  const label = PROPERTY_TYPE_LABEL_MAP[type];
  return label && isCustom ? `Custom ${label}` : label;
}

/** For object properties */
function getPropertyObjectKeySuffix(propertyObjectKey, {isExperiments} = {isExperiments: false}) {
  if (!propertyObjectKey) {
    return ``;
  }

  if (isExperiments) {
    // special case for $experiments
    const experimentId = Array.isArray(propertyObjectKey) ? propertyObjectKey[0] : propertyObjectKey;
    return `${objectAccessSymbol}${renamePropertyValue(experimentId, MappedPropertyValues.ExperimentId)}`;
  }

  return `${objectAccessSymbol}${
    Array.isArray(propertyObjectKey) ? propertyObjectKey.join(objectAccessSymbol) : propertyObjectKey
  }`;
}

/** For list of object properties */
function getListItemGroupSuffix(listItemGroup) {
  if (!listItemGroup) {
    return ``;
  }

  // insights format uses "value" for the property name
  const subPropertyName = _renameProperty(
    listItemGroup.value ?? listItemGroup.propertyName,
    listItemGroup.resourceType,
  );
  const suffix = `${listObjectAccessSymbol}${subPropertyName}${getPropertyObjectKeySuffix(
    listItemGroup.propertyObjectKey,
  )}`;
  // recursion allows nested lists
  return suffix + getListItemGroupSuffix(listItemGroup.listItemGroup);
}

function _renameProperty(
  propertyName: string | string[],
  resourceType: ResourceType,
  {
    dataGroupId = null,
    customProperty = null,
  }: {dataGroupId?: any; customProperty?: any; propertyObjectKey?: any; listItemGroup?: any} = {},
): string {
  if (customProperty && customProperty.name) {
    if (Array.isArray(propertyName)) {
      return [customProperty.name, _renameProperty(propertyName[1], resourceType, {dataGroupId})].join(`.`);
    } else {
      return customProperty.name;
    }
  } else if (Array.isArray(propertyName)) {
    return [
      _renameProperty(propertyName[0], resourceType),
      _renameProperty(propertyName[1], resourceType, {dataGroupId, customProperty}),
    ].join(objectAccessSymbol);
  } else {
    const renamedProperty = getPropertyDisplayName(propertyName, resourceType, dataGroupId);
    if (renamedProperty !== propertyName) {
      return renamedProperty;
    }

    // default conversion for all properties starting with '$'
    if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
      return propertyName
        .slice(1)
        .split(`_`)
        .map((str) => capitalize(str))
        .join(` `);
    }
    return propertyName;
  }
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
function renameProperty(
  propertyName: string | string[],
  resourceType: ResourceType,
  {
    dataGroupId = null,
    customProperty = null,
    propertyObjectKey = null,
    listItemGroup = null,
  }: {dataGroupId?: any; customProperty?: any; propertyObjectKey?: any; listItemGroup?: any} = {},
): string {
  const isExperiments = propertyName === MPProperties.Experiments;
  return (
    _renameProperty(propertyName, resourceType, {dataGroupId, customProperty}) +
    getPropertyObjectKeySuffix(propertyObjectKey, {isExperiments}) +
    getListItemGroupSuffix(listItemGroup)
  );
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
// TODO: renameProperty and getPropertyDisplayName are flawed. It should be checking against
// the right property definition store based on resourceType ('event' or 'user'), which they
// currently cannot do only taking the propertyName as argument
/**
 * @deprecated - use renameProperty instead
 */
export function renamePropertyOld(
  propertyName: string | string[],
  {dataGroupId = null, customProperty = null} = {},
): string {
  if (customProperty && customProperty.name) {
    if (Array.isArray(propertyName)) {
      return [customProperty.name, renamePropertyOld(propertyName[1], {dataGroupId})].join(`.`);
    } else {
      return customProperty.name;
    }
  } else if (Array.isArray(propertyName)) {
    return [renamePropertyOld(propertyName[0]), renamePropertyOld(propertyName[1], {dataGroupId, customProperty})].join(
      `.`,
    );
  } else {
    const renamedProperty = getPropertyDisplayNameOld(propertyName);
    if (renamedProperty !== propertyName) {
      return renamedProperty;
    }

    // default conversion for all properties starting with '$'
    if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
      return propertyName
        .slice(1)
        .split(`_`)
        .map((str) => capitalize(str))
        .join(` `);
    }
    return propertyName;
  }
}

export function getMissingDatagroupMappedProperty(data): string {
  return (
    renamePropertyOld(data.join_property_name) +
    `.` +
    renamePropertyOld(data.datagroup_property_name, {dataGroupId: data.data_group_id})
  );
}

export function renamePropertyValue(value, propertyName): string {
  if (propertyName === `$event_name` || propertyName === `$event`) {
    // Value is an event
    return renameEvent(value);
  } else if (propertyName === `$people`) {
    // Value is a property
    return renamePropertyOld(value); // value is $all_people
  }

  // check against special property values (empty list, undefined, emtpy string, etc...)
  const constantPropertyValue = getOwnAttr(ConstantPropertyValuesInfo, value);
  if (constantPropertyValue) {
    return constantPropertyValue.label;
  }

  const id = parseInt(value, 10);
  const propertyValueMappings = getPropertyValueMappings();
  if (!propertyValueMappings || Number.isNaN(id)) {
    return value;
  }

  let mappedPropertyValue = value;
  const {campaignVariants = [], experiments = [], datasets = {}} = propertyValueMappings;
  switch (propertyName) {
    case MappedPropertyValues.Campaigns:
    case MappedPropertyValues.CampaignId: {
      // map campaign IDs to their names
      const campaign = campaignVariants.find((c) => c.id === id);
      if (campaign) {
        mappedPropertyValue = campaign.name;
      }
      break;
    }
    case MappedPropertyValues.MessageId: {
      // map message variant IDs to their numbers and campaign
      const campaign = campaignVariants.find((c) => c.variants.includes(id));
      if (campaign) {
        const variantIdx = campaign.variants.indexOf(id);
        mappedPropertyValue =
          variantIdx === 0 ? `${campaign.name} - Original variant` : `${campaign.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case MappedPropertyValues.ExperimentId: {
      // map experiment IDs to their names
      const experiment = experiments.find((exp) => exp.id === id);
      if (experiment) {
        mappedPropertyValue = experiment.name;
      }
      break;
    }
    case MappedPropertyValues.VariantId: {
      // map experiment variant IDs to their numbers and experiment
      const experiment = experiments.find((exp) => exp.variants.some((variant) => variant.id === id));
      if (experiment) {
        const variantIdx = experiment.variants
          .map((variant) => variant.id)
          .sort((a, b) => a - b)
          .indexOf(id);
        mappedPropertyValue =
          variantIdx === 0 ? `${experiment.name} - Original variant` : `${experiment.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case MappedPropertyValues.Dataset: {
      const dataset = datasets[value];
      if (dataset) {
        mappedPropertyValue = `${dataset.name} (id: ${dataset.projectId})`;
      }
      break;
    }
    default:
      break;
  }

  return mappedPropertyValue;
}
